/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { CommunicationListItem } from '@gh/builder-shared';
import {
  BaseDesignRestService,
  ChecklistRestService,
  CommunicationRestService,
  CommunicationType,
  ContactCustomerType,
  ContactRestService,
  JobRestService,
  LeadRestService,
  LetterRestService,
  MeetingRestService,
  QuotationRestService,
  SalesFolderRestService,
  SalesFolderStatus,
  SiteRestService,
  SystemRoleBit,
  systemRolesBitSet,
  UserRestService,
} from '@gh/builder-shared-data';
import {
  dictionaryItemFromDto,
  DictionaryService,
  getRouteParam,
  globalDictionaries,
  InitViewService,
  isDirtyTypeNew,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { AutocompleteItem, EntityVersion, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData, RouteDataService } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';

import { find, get, isNil } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  JobDppDetails,
  jobDppDetailsToCreateDto,
  LeadApprovedQuote,
  LeadContactDetails,
  LeadDetailsInit,
  leadDetailsInitFromDto,
  LeadDetailsSave,
  LeadDetailsSaveResponse,
  leadDetailsSaveToDto,
  LeadSalesFolderComplete,
  LeadSalesFolderFinalVersionLetter,
  LeadSalesFolderLetter,
  LeadSalesFolderMeeting,
  SfCompletionQuoteListItem,
  SfCompletionRequest,
  sortCompletionQuotes,
  UserDetailsShort,
} from './lead.model';
import { SiteListItem } from './quote.model';
import { sortByStringField } from '@gh/core-util';

export interface ApprovedQuotesDialogConfig {
  quotes: LeadApprovedQuote[];
}

export interface CompleteSaleDialogConfig {
  customerName: string;
  salesFolderId: ID;
  salesFolderName: string;
  dppJobNumber?: number;
  dppBaseDesign?: AutocompleteItem;
  dppMemo?: string;
}

export interface JobDppDetailsDialogConfig {
  customerName: string;
  salesFolderId: ID;
  salesFolderName: string;
  address: string;
}

@Injectable()
export class LeadDetailsBusinessService implements InitViewService {
  activityWasChanges: BehaviorSubject<ID | undefined | null>;
  constructor(private leadRest: LeadRestService,
              private siteRest: SiteRestService,
              private salesFolderRest: SalesFolderRestService,
              private meetingRest: MeetingRestService,
              private contactRest: ContactRestService,
              private userRest: UserRestService,
              private letterRest: LetterRestService,
              private dictionaryService: DictionaryService,
              private routeDataService: RouteDataService,
              private quotationRest: QuotationRestService,
              private checklistRest: ChecklistRestService,
              private jobRest: JobRestService,
              private baseDesignRest: BaseDesignRestService,
              private communicationService: CommunicationRestService) {
    this.activityWasChanges = new BehaviorSubject(null);
  }

  findCustomersContactsByName(query: FilteredDataQuery<string>): Observable<LeadContactDetails[]> {
    return this.contactRest.findAllCustomersAsDataSource({
      name: query.filter,
    }).select(query).pipe(map$(getSlicedData));
  }

  findSalesPersonsByName(query: FilteredDataQuery<string>, excludedSalesPersonId?: ID): Observable<UserDetailsShort[]> {
    return this.userRest.findAllAsDataSource({
      name: query.filter,
      showInactive: false,
      autocomplete: true,
      excludeIds: excludedSalesPersonId ? [excludedSalesPersonId] : [],
      systemRoles: systemRolesBitSet(SystemRoleBit.SalesPerson),
    }).select(query).pipe(map$(getSlicedData));
  }

  findAllApprovedRfqNumbers(salesFolderId: ID): Observable<LeadApprovedQuote[]> {
    return this.quotationRest.findAllApprovedRfqNumbersBySalesFolderId(salesFolderId);
  }

  findAllQuotes(salesFolderId: ID): Observable<SfCompletionQuoteListItem[]> {
    return this.quotationRest.findAllBySalesFolderId(salesFolderId).pipe(map$(({ quotations }) => quotations));
  }

  findAllSites(salesFolderId: ID): Observable<SiteListItem[]> {
    return this.siteRest.findAll(salesFolderId).pipe(map$((list) => list.sites));
  }

  initView(route: ActivatedRouteSnapshot): any {
    const tab = route.queryParams.tab;
    const salesFolderId = route.queryParams.salesFolderId;
    return {
      details: isRouteForCreationMode(route) ? this.getInit() : this.get(getRouteParam(route, 'leadId')),
      tab,
      salesFolderId,
    };
  }

  initSalesFolderTab(): any {
    return {
      notProceedingReasons: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons)
        .pipe(map$((items) => items ? items.map(dictionaryItemFromDto) : [])),
      priorities: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority)
        .pipe(map$((priorities) =>
                     sortByStringField(priorities, 'localization').map((item) =>
                                                                         ({name: item.localization, id: parseInt(item.itemCode) })))),
      stages: this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage)
        .pipe(map$((priorities) =>
                     sortByStringField(priorities, 'itemCode').map((item) => ({name: item.localization, id: parseInt(item.itemCode) })))),
      statuses: this.dictionaryService.getByEnum(SalesFolderStatus),
    };
  }

  initDcTab(): any {
    return {
      customerTypes: this.dictionaryService.getByEnum(ContactCustomerType),
      communicationTypes: this.dictionaryService.getByEnum(CommunicationType),
    };
  }

  initCompleteSaleDialogView(route: DialogRouteSnapshot<CompleteSaleDialogConfig>): any {
    return {
      quotes: this.findAllQuotes(route.input.salesFolderId).pipe(map$(sortCompletionQuotes)),
      checklist: this.checklistRest.findAll(),
    };
  }

  get(id: ID): Observable<LeadDetailsInit> {
    return this.leadRest.get(id).pipe(map$(leadDetailsInitFromDto));
  }

  getInit(): Observable<LeadDetailsInit> {
    return this.leadRest.getInit().pipe(map$(leadDetailsInitFromDto));
  }

  getCompleteSalesFolder(folderId: ID): Observable<LeadSalesFolderComplete> {
    return this.salesFolderRest.get(folderId);
  }

  getCommunicationDetails(id: ID): Observable<CommunicationListItem> {
    return this.communicationService.get(id);
  }

  save(model: LeadDetailsSave): Observable<LeadDetailsSaveResponse> {
    const save$ = isDirtyTypeNew(model.dirtyType)
      ? this.leadRest.create(leadDetailsSaveToDto(model)) : this.leadRest.update(leadDetailsSaveToDto(model));

    // load current sales folder if it is necessary
    return save$.pipe(switchMap$((saveResponseDto) => {
      const { salesFolder } = model;
      if (isNil(salesFolder) || isNil(salesFolder.dirtyType)) {
        return of$(leadDetailsInitFromDto(saveResponseDto));
      }

      // if it is new sales folder we have to find its id in received sales folder list
      const salesFolderId = get(
        isDirtyTypeNew(salesFolder.dirtyType) ? find(saveResponseDto.salesFolders, { isNew: true }) : salesFolder,
        'salesFolderId');
      
      return this.getCompleteSalesFolder(salesFolderId!).pipe(map$((loadedSalesFolder) => ({
        ...leadDetailsInitFromDto(saveResponseDto),
        currentSalesFolder: loadedSalesFolder,
      })));
    }));
  }

  generateFirstContactLetterTemplate(salesFolderId: ID): Observable<LeadSalesFolderLetter> {
    return this.letterRest.prepareFirstContactLetter(salesFolderId);
  }

  generateFirstContactLetter(letter: LeadSalesFolderFinalVersionLetter): Observable<LeadSalesFolderLetter> {
    return this.letterRest.buildFinalVersion(letter).pipe(
      map$((generated) => ({ ...generated, deliveryBy: letter.filledTemplate.deliveryBy })));
  }

  generateAuthorityToProceedLetter(salesFolderId: ID, quoteId?: ID): Observable<LeadSalesFolderLetter> {
    return this.letterRest.buildAuthorityToProceedLetter(salesFolderId, quoteId);
  }

  reassign(leadId: ID, version: EntityVersion, salesPersonId: ID): Observable<LeadDetailsInit> {
    return this.leadRest.reassign(leadId, version, salesPersonId).pipe(map$(leadDetailsInitFromDto));
  }

  readSms(id: ID): Observable<any> {
    return this.communicationService.setSmsAsRead(id);
  }

  createMeeting(sfId: ID, meeting: LeadSalesFolderMeeting): Observable<LeadSalesFolderMeeting> {
    return this.meetingRest.create(sfId, meeting);
  }

  updateMeeting(sfId: ID, meeting: LeadSalesFolderMeeting): Observable<LeadSalesFolderMeeting> {
    return this.meetingRest.update(sfId, meeting);
  }

  saveMeeting(sfId: ID, meeting: LeadSalesFolderMeeting): Observable<LeadSalesFolderMeeting> {
    return meeting.id ? this.updateMeeting(sfId, meeting) : this.createMeeting(sfId, meeting);
  }

  deleteMeeting(sfId: ID, meeting: LeadSalesFolderMeeting): Observable<void> {
    return this.meetingRest.remove(sfId, meeting);
  }

  completeQuote(sfId: ID, complete: SfCompletionRequest): Observable<void> {
    return this.salesFolderRest.complete(sfId, complete);
  }

  findAllBaseDesignsByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.baseDesignRest.findAllAsDataSource({
      searchString: query.filter,
      showDeleted: false,
    }).select(query).pipe(map$(getSlicedData));
  }

  createDppJob(sfId: ID, details: JobDppDetails): Observable<void> {
    return this.salesFolderRest.createDppJob(sfId, jobDppDetailsToCreateDto(details));
  }
}
